import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Mail, Lock, MapPin, Camera, Trash2, Check, Loader2, Quote, Calendar, Bell } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Page, PageTitle, Card, GoldButton, GhostButton, Loader } from "../components/UI";
import { PlayerAvatar } from "../components/PlayerAvatar";
import { LinkedPartners } from "../components/LinkedPartners";
import FriendsSection from "../components/FriendsSection";
import CertificateSection from "../components/CertificateSection";
import { Input } from "./Login";

function SectionCard({ icon: Icon, title, desc, children, testid }) {
  return (
    <Card className="p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-[#E5B842]" />
        <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-50">{title}</h2>
      </div>
      {desc && <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-4">{desc}</p>}
      <div className={desc ? "" : "mt-4"}>{children}</div>
    </Card>
  );
}

function NotificationPrefs({ initial }) {
  const [on, setOn] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => setOn(initial), [initial]);
  const toggle = async (v) => {
    setOn(v); setBusy(true);
    try {
      await api.patch("/me/notification-prefs", { kickoff_reminders: v });
      toast.success(v ? "Ειδοποιήσεις αγώνων: ΕΝΕΡΓΕΣ" : "Ειδοποιήσεις αγώνων: ΑΝΕΝΕΡΓΕΣ");
    } catch (e) { setOn(!v); toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <SectionCard icon={Bell} title="Ειδοποιήσεις" desc="Notifications" testid="settings-notifications">
      <div className="flex items-center justify-between gap-4 rounded-lg border border-[#2b2512] bg-black/40 px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-slate-100">Ειδοποιήσεις αγώνων</div>
          <div className="text-xs text-slate-400 mt-0.5">Λάβετε υπενθύμιση 24 ώρες πριν τον αγώνα της ομάδας σας</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-mono uppercase tracking-wider ${on ? "text-[#E5B842]" : "text-slate-500"}`} data-testid="settings-kickoff-reminders-state">{on ? "ON" : "OFF"}</span>
          <Switch checked={on} onCheckedChange={toggle} disabled={busy} data-testid="settings-kickoff-reminders-switch"
            className="data-[state=checked]:bg-[#E5B842] data-[state=unchecked]:bg-slate-700" />
        </div>
      </div>
    </SectionCard>
  );
}

export default function SettingsPage() {
  const { user, player, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [countries, setCountries] = useState([]);
  const fileRef = useRef(null);
  const aliveRef = useRef(true);
  const [drag, setDrag] = useState(false);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confPw, setConfPw] = useState("");
  const [iso3, setIso3] = useState("");
  const [city, setCity] = useState("");
  const [tagline, setTagline] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/meta").then(({ data }) => setCountries(data.countries || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (user) { setName(user.display_name || ""); setEmail(user.email || ""); }
    if (player) { setIso3(player.country?.iso3 || ""); setCity(player.city || ""); setTagline(player.tagline || ""); setBirthdate(player.birthdate || ""); }
  }, [user, player]);

  if (!user) return <Loader />;

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const saveName = () => run("name", async () => {
    await api.patch("/me/display-name", { display_name: name });
    await refresh(); toast.success("Display name updated");
  });
  const saveEmail = () => run("email", async () => {
    await api.patch("/me/email", { email });
    await refresh(); toast.success("Email updated");
  });
  const savePassword = () => run("pw", async () => {
    const { data } = await api.patch("/me/password", { current_password: curPw, new_password: newPw, confirm_password: confPw });
    toast.success("Password changed — please sign in again");
    if (data.require_relogin) { logout(); nav("/login"); }
  });
  const saveLocation = () => run("loc", async () => {
    await api.patch("/me/location", { country_iso3: iso3, city });
    await refresh(); toast.success("Location updated");
  });
  const saveTagline = () => run("tag", async () => {
    await api.patch("/me/tagline", { tagline });
    await refresh(); toast.success("Promo line updated");
  });
  const saveBirthdate = () => run("dob", async () => {
    await api.patch("/me/birthdate", { birthdate });
    await refresh(); toast.success("Date of birth updated");
  });

  const AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp"];
  const validAvatar = (f) => {
    const typeOk = AVATAR_TYPES.includes(f.type) || (!f.type && /\.(jpe?g|png|webp)$/i.test(f.name));
    if (!typeOk) { toast.error("Μη έγκυρο αρχείο — επιτρέπονται μόνο εικόνες JPG, PNG ή WebP."); return false; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Η εικόνα ξεπερνά το όριο των 5 MB."); return false; }
    return true;
  };
  // Cinematic portrait is generated in the background; swap it in when ready.
  const pollCinematic = async (startUrl) => {
    for (let i = 0; i < 24; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      if (!aliveRef.current) return;
      try {
        const { data } = await api.get("/auth/me");
        if (data.player?.avatar_url !== startUrl || data.player?.avatar_cinematic_status === "failed") { await refresh(); return; }
      } catch (e) { return; }
    }
  };
  const uploadAvatar = async (f) => {
    if (!f || !validAvatar(f)) return;
    await run("avatar", async () => {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/me/avatar", fd);
      await refresh(); toast.success("Η φωτογραφία ενημερώθηκε");
      if (data.cinematic === "pending") pollCinematic(data.avatar_url);
    });
  };
  const onPickFile = (e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadAvatar(f); };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); uploadAvatar(e.dataTransfer.files?.[0]); };
  const removeAvatar = () => run("avatar", async () => {
    await api.delete("/me/avatar");
    await refresh(); toast.success("Avatar removed");
  });

  const Spin = ({ k }) => busy === k ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />;

  return (
    <Page>
      <PageTitle eyebrow="Account" title="Settings" subtitle="Manage your WSTF identity, credentials and avatar." />
      <div className="max-w-3xl space-y-6" data-testid="settings-page">

        {/* AVATAR */}
        <SectionCard icon={Camera} title="Avatar" desc="Your photo across the platform — JPG, PNG or WebP, up to 5 MB" testid="settings-avatar">
          <div
            onClick={() => busy !== "avatar" && fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); if (!drag) setDrag(true); }}
            onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDrag(false); }}
            onDrop={onDrop}
            role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileRef.current?.click(); } }}
            data-testid="settings-avatar-dropzone" data-drag={drag ? "over" : "idle"}
            className={`flex items-center gap-5 rounded-xl border-2 border-dashed p-4 cursor-pointer ${drag ? "border-[#E5B842] bg-[#E5B842]/10 scale-[1.01]" : "border-[#2b2512] hover:border-[#E5B842]/50"}`}
            style={{ transition: "border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease" }}>
            <PlayerAvatar player={player} size={88} testid="settings-avatar-preview" />
            <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPickFile} className="hidden" data-testid="settings-avatar-input" />
              <GoldButton onClick={() => fileRef.current?.click()} disabled={busy === "avatar"} data-testid="settings-avatar-upload">
                {busy === "avatar" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Upload photo
              </GoldButton>
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500" data-testid="settings-avatar-drop-hint">
                {drag ? "Αφήστε την εικόνα εδώ" : "ή σύρετε & αφήστε μια φωτογραφία εδώ"}
              </span>
              {player?.avatar_url && (
                <button onClick={removeAvatar} disabled={busy === "avatar"} data-testid="settings-avatar-remove"
                  className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-rose-400">
                  <Trash2 className="w-3.5 h-3.5" /> Remove — revert to initials
                </button>
              )}
            </div>
          </div>
        </SectionCard>

        {/* DISPLAY NAME */}
        <SectionCard icon={User} title="Display name" testid="settings-name">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1"><Input label="Display name" value={name} onChange={setName} testid="settings-name-input" /></div>
            <GoldButton onClick={saveName} disabled={busy === "name" || !name.trim()} data-testid="settings-name-save"><Spin k="name" /> Save</GoldButton>
          </div>
        </SectionCard>

        {/* EMAIL */}
        <SectionCard icon={Mail} title="Email" desc="Your login identifier — your session stays active after changing it" testid="settings-email">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1"><Input label="Email" type="email" value={email} onChange={setEmail} testid="settings-email-input" /></div>
            <GoldButton onClick={saveEmail} disabled={busy === "email" || !email.trim()} data-testid="settings-email-save"><Spin k="email" /> Save</GoldButton>
          </div>
        </SectionCard>

        {/* PASSWORD */}
        <SectionCard icon={Lock} title="Password" desc="You'll be asked to sign in again after changing it" testid="settings-password">
          <div data-testid="settings-google-status" className="mb-4 flex items-center gap-2 text-xs">
            <span className={`w-2 h-2 rounded-full ${user?.google_linked ? "bg-emerald-400" : "bg-slate-600"}`} />
            <span className="text-slate-300">{user?.google_linked ? `Google sign-in linked (${user.email})` : "Google sign-in not linked — sign in with Google using this email to link it"}</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Input label="Current password" type="password" value={curPw} onChange={setCurPw} testid="settings-current-password" />
            <Input label="New password" type="password" value={newPw} onChange={setNewPw} testid="settings-new-password" />
            <Input label="Confirm new" type="password" value={confPw} onChange={setConfPw} testid="settings-confirm-password" />
          </div>
          <div className="mt-4">
            <GoldButton onClick={savePassword} disabled={busy === "pw" || !curPw || newPw.length < 8 || !confPw} data-testid="settings-password-save"><Spin k="pw" /> Change password</GoldButton>
          </div>
        </SectionCard>

        {/* LOCATION */}
        <SectionCard icon={MapPin} title="Country & City" desc="Updates your profile only — your WSTF ID never changes" testid="settings-location">
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 sm:items-end">
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-mono">Country</span>
              <select value={iso3} onChange={(e) => setIso3(e.target.value)} data-testid="settings-country-select"
                className="mt-1.5 w-full bg-[#000000] border border-[#2b2512] rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-[#E5B842]/50">
                <option value="">Select a country</option>
                {countries.map((c) => <option key={c.iso3} value={c.iso3}>{c.name}</option>)}
              </select>
            </label>
            <Input label="City" value={city} onChange={setCity} testid="settings-city-input" placeholder="Athens" />
            <GoldButton onClick={saveLocation} disabled={busy === "loc" || !iso3} data-testid="settings-location-save"><Spin k="loc" /> Save</GoldButton>
          </div>
        </SectionCard>

        {/* PROMO TAGLINE */}
        <SectionCard icon={Quote} title="Your promo line" desc="A short gold tagline shown on your player passport — your words, max 60 characters" testid="settings-tagline">
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 sm:items-end">
            <Input label="Promo line" value={tagline} onChange={(v) => setTagline(v.slice(0, 60))} testid="settings-tagline-input" placeholder="More than a game" />
            <GoldButton onClick={saveTagline} disabled={busy === "tag"} data-testid="settings-tagline-save"><Spin k="tag" /> Save</GoldButton>
          </div>
          <div className="mt-1.5 text-[11px] font-mono text-slate-600">{tagline.length}/60 · leave empty to show nothing</div>
        </SectionCard>

        {/* DATE OF BIRTH */}
        <SectionCard icon={Calendar} title="Date of birth" desc="Only your AGE is shown on your public passport — never the full date. Leave empty to hide age." testid="settings-birthdate">
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 sm:items-end">
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-mono">Birth date</span>
              <input type="date" value={birthdate} max="2015-12-31" onChange={(e) => setBirthdate(e.target.value)} data-testid="settings-birthdate-input"
                className="mt-1.5 w-full bg-[#000000] border border-[#2b2512] rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-[#E5B842]/50" />
            </label>
            <GoldButton onClick={saveBirthdate} disabled={busy === "dob"} data-testid="settings-birthdate-save"><Spin k="dob" /> Save</GoldButton>
          </div>
        </SectionCard>

        {/* NOTIFICATIONS */}
        <NotificationPrefs initial={user.notification_prefs?.kickoff_reminders !== false} />

        {/* CERTIFICATE OF MEMBERSHIP */}
        <CertificateSection />

        {/* FRIENDS */}
        <FriendsSection />

        {/* LINKED PARTNERS */}
        <LinkedPartners />
      </div>
    </Page>
  );
}
